'use client';

import { useEffect, useRef, useState } from 'react';

interface Props {
  threshold?: number | number[];
  rootMargin?: string;
  triggerOnce?: boolean;
  onImpression?: (entry: IntersectionObserverEntry) => void;
}

interface ReturnType<T extends Element = HTMLDivElement> {
  ref: React.RefObject<T>;
  isVisible: boolean;
}

export const useTrackImpression = <T extends Element = HTMLDivElement>(
  options: Props = {} as Props,
): ReturnType<T> => {
  const { threshold = 0.1, rootMargin = '0px', triggerOnce = true, onImpression } = options;

  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 이미 트리거되었고 triggerOnce가 true면 무시
            if (triggerOnce && hasTriggered) return;

            setIsVisible(true);
            setHasTriggered(true);
            // 커스텀 콜백 호출
            onImpression?.(entry);
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        });
      },
      {
        threshold,
        rootMargin,
      },
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce, hasTriggered, onImpression, ref]);

  return {
    ref,
    isVisible,
  };
};
